<template>
    <div v-dusk="'force-preparation-datepicker'" class="force-preparation-datepicker row">
        <div v-if="loading" class="col-md-12">
            <i class="fas fa-spinner fa-spin"></i> Calcul de la date de fin de préparation en cours...
        </div>
        <div v-else-if="errors && errors.length" class="col-md-12">
            <div class="alert alert-danger">
                <template v-for="error in errors">
                    <span>{{ error }}</span>
                </template>
                <br>
                Essayez de sélectionner une nouvelle date.
            </div>
        </div>
        <template v-else>
            <div class="col-md-4">
                <BootstrapDatepicker
                    ref="datepicker"
                    :disabled-dates="disabledDates"
                    :day-cell-content="getDayCellContent"
                    :disabled="disabled"
                    :input-class="'form-control-plaintext'"
                    :calendar-button="false"
                    :open-date="selectedDate"
                    v-model="selectedDate"
                    @opened="onOpen"
                    @closed="onClose"
                />
            </div>
            <div v-if="!hideButton" class="col-md-8">
                <button :disabled="!canSelectRedDates" type="button" class="btn" :class="canSelectRedDates ? 'btn-outline-danger' : 'btn-outline-secondary'" @click="open">
                    Modifier la date
                </button>
            </div>
        </template>
    </div>
</template>

<script>
    import {momentDay, momentDaysAreSame} from "../moment-with-helpers";
    import {useGetLoader} from "./compositions/axios/loadingRequest";

    import BootstrapDatepicker from "./BootstrapDatepicker";

    export default {
        components: {
            BootstrapDatepicker,
        },

        props: {
            customer: {
                type: Object,
                required: true,
            },
            value: {
                type: Object,
                required: false,
            },
            deliveryDate: {
                type: Object,
                required: true,
            },
            canSelectRedDates: {
                type: Boolean,
                required: false,
            },
            hideButton: {
                type: Boolean,
                required: false,
                default: false,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loaded,
                loading,
                errors,
                load,
            };
        },

        data() {
            return {
                selectedDate: this.value && momentDay(this.value.date).toDate(),
                calculatedPreparationDate: null,

                disabled: true,
            };
        },

        created() {
            if (!this.selectedDate) {
                this.loadEndPreparationDate();
            }
        },

        methods: {
            getDayCellContent(params) {
                const color = this.getDateType(new Date(params.timestamp)) === 'forced' ? 'blank' : 'green';
                return `<span class="day-${color}">${params.date}</span>`;
            },

            getDateType(date) {
                if (momentDaysAreSame(this.calculatedPreparationDate, date)) {
                    return 'calculated';
                }
                return 'forced';
            },

            onOpen() {
                this.$emit('opened');
            },

            onClose() {
                this.disabled = true;

                this.$emit('closed');
            },

            open() {
                this.disabled = false;

                this.$nextTick(() => {
                    this.$refs.datepicker.close();
                    this.$refs.datepicker.showCalendar();
                    this.$refs.datepicker.$el.querySelector('input').focus();
                    this.$refs.datepicker.$el.querySelector('input').addEventListener('blur', () => {
                        this.onClose();
                    });

                    this.$emit('opened');
                });
            },

            async loadEndPreparationDate() {
                const formattedDate = momentDay(this.deliveryDate.date).format('YYYY-MM-DD');

                const data = await this.load(`/espace-employes/commandes/calcul-date-fin-preparation/${this.customer.type}/${this.customer.id}/${formattedDate}`);

                if (data) {
                    this.calculatedPreparationDate = this.selectedDate = momentDay(data.end_preparation_date, "YYYY-MM-DD").toDate();
                }
            },

            buildEventDateChangePayload(date) {
                if (date) {
                    const type = this.getDateType(date);
                    return {date: momentDay(date).toDate(), type: type};
                }
                return null;

            },
        },

        watch: {
            customer() {
                this.selectedDate = null;
            },

            deliveryDate(newDate, oldDate) {
                this.selectedDate = null;
                if (this.customer && newDate) {
                    this.loadEndPreparationDate();
                }
            },

            selectedDate(newDate, oldDate) {
                if ((newDate === null && oldDate !== null)
                    || (newDate !== null && oldDate === null)
                    || (newDate !== null && oldDate !== null && !momentDaysAreSame(newDate, oldDate))) {
                    this.$emit('input', this.buildEventDateChangePayload(newDate));
                }
            },
        },

        computed: {
            disabledDates() {
                if (this.deliveryDate) {
                    return {
                        from: momentDay(this.deliveryDate.date).toDate()
                    };
                }
            },
        }
    }
</script>

<style lang="scss">
    .vdp-datepicker {
        $green: #2ecc71;

        .cell {
            padding: 0 !important;
        }

        .vdp-datepicker__calendar .cell.selected {
            background: transparent;
        }

        .cell.day span {
            display: block;
            width: 100%;
            height: 100%;
            line-height: 37px;
        }

        .cell.day:not(.disabled) .day-green {
            border: 2px solid $green;
        }

        .cell.day:not(.disabled):hover {
            border: 1px solid transparent;
        }

        .cell.day:not(.disabled):hover .day-green {
            background-color: $green;
        }

        .cell.day.selected .day-green {
            background-color: $green;
        }

        .cell.day.disabled:hover {
            cursor: not-allowed;
        }

        .cell.day.disabled.selected .day-green {
            background-color: desaturate($green, 50%);
        }

        .cell.day.disabled .day-green {
            border: 2px solid desaturate($green, 50%);
        }
    }
</style>
